import { Layers, Zap, ShieldCheck, Cloud, Package, ArrowRight, ArrowUp, ArrowDown, CheckCircle2, XCircle, Code2, ServerCog, GitBranch, Download, ChevronDown, Sparkles, Users, LineChart, MessageSquare } from "lucide-react";
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useBranding } from "@/lib/branding";

const OPTIONS = [
  {
    id: "current",
    name: "React + FastAPI (current)",
    tag: "Recommended for pitch / go-live",
    accent: true,
    stack: ["React 19", "FastAPI · Python 3.11", "MongoDB (Motor)", "Flexspace Copilot", "Resend", "shadcn/ui + Tailwind"],
    pros: ["Fully built today", "Best-in-class UX & branding", "Modern LLM Copilot", "Deployable anywhere (Emergent · Vercel · AWS · Azure · GCP · Chevron on-prem)"],
    cons: ["Chevron hosts + maintains a second app", "Requires Python + Mongo ops"],
    effort: "Ready now — ~2 weeks to wire real ITSM auth",
    ownership: "Digital / Platform team",
  },
  {
    id: "sn",
    name: "ServiceNow-native",
    tag: "Best if SN CoE owns everything",
    stack: ["Employee Center + Service Portal", "sc_request + sc_req_item + custom u_flexspace_* tables", "Flow Designer (approvals)", "UI Builder / Now Experience widgets", "Now Assist (extra license) for AI"],
    pros: ["Zero integration — data lives natively in SN", "Chevron SN admins can maintain", "Native sysapproval_approver + audit", "SSO + governance inherited"],
    cons: ["Slower iteration (Studio + Flow Designer)", "Harder to match modern branding", "LLM story weaker unless Now Assist purchased"],
    effort: "~8-12 weeks · 2-3 SN devs",
    ownership: "ServiceNow CoE",
  },
  {
    id: "power",
    name: "Microsoft Power Platform",
    tag: "Best if Chevron owns M365 already",
    stack: ["Canvas App + Model-driven", "Dataverse tables", "Power Automate (approvals + connectors)", "Copilot Studio + Azure OpenAI", "Power BI (dashboards)", "Entra ID SSO"],
    pros: ["Entra ID SSO free", "Teams/Outlook one-click approvals", "Zero-infra (Microsoft-hosted)", "Certified ServiceNow connector", "Citizen-dev friendly"],
    cons: ["Design polish constrained", "Per-user Premium licensing ($20+/mo)", "Copilot Studio reasoning constrained", "Slow with large datasets"],
    effort: "~3 months · 2 Power Platform devs",
    ownership: "M365 / Modern Workplace team",
  },
  {
    id: "bigfix",
    name: "BigFix Service Management",
    tag: "Only if BigFix is the SoR",
    stack: ["BigFix WebUI extensions", "BigFix REST API", "HCL Workflow"],
    pros: ["Native tie-in to BigFix ops", "Same platform as endpoint mgmt"],
    cons: ["Weaker portal tooling than ServiceNow", "Rarely used stand-alone for employee UX", "Small community"],
    effort: "~12-16 weeks",
    ownership: "IT Ops (endpoint) team",
  },
];

const MAPPING = [
  { flex: "Persona picker + login", sn: "Employee Center + Now user profile", power: "Entra ID + Dataverse role", bigfix: "BigFix account + LDAP" },
  { flex: "Device Catalogue", sn: "sc_cat_item + sc_category", power: "Dataverse + Canvas Gallery", bigfix: "BigFix Custom List" },
  { flex: "OrderDialog + REQ", sn: "Order Guide → sc_request", power: "Canvas Form + Dataverse row", bigfix: "REST POST /requests" },
  { flex: "Approval workflow", sn: "sysapproval_approver + Flow Designer", power: "Approvals Connector (Teams cards)", bigfix: "HCL Workflow" },
  { flex: "ITSM Payload viewer", sn: "Native — no viewer needed", power: "Power Automate history + Run details", bigfix: "Audit log" },
  { flex: "Ask Me Copilot", sn: "Now Assist (paid)", power: "Copilot Studio + Azure OpenAI", bigfix: "Not native — external API" },
  { flex: "Device UX / Nexthink", sn: "Nexthink Spoke (paid)", power: "Power Automate HTTP → Nexthink", bigfix: "Native BigFix analytics" },
  { flex: "Refresh Radar", sn: "Scheduled script + report", power: "Dataflow + Power BI tile", bigfix: "BigFix Fixlet + report" },
  { flex: "Buyback / Sustainability", sn: "Custom scoped app", power: "Canvas screen + calc column", bigfix: "Not native" },
  { flex: "Cost / Ariba integration", sn: "SN Spoke for Ariba (paid)", power: "Power Automate Ariba connector", bigfix: "Custom REST" },
  { flex: "OEM Factory integration", sn: "Custom REST + MID Server", power: "Power Automate HTTP", bigfix: "Custom REST" },
];

const HANDOFF = [
  { phase: "Week 0", title: "Deal Review + Architecture pick", items: ["Demo current React build to Chevron IT + Procurement + CIO", "Agree on target stack (Path A/B/C/D)", "Confirm SSO source (Entra ID / Okta)"] },
  { phase: "Week 1-2", title: "Data model + Contracts", items: ["Freeze Dataverse / SN table shapes", "Sign off REST contracts (ServiceNow, Ariba, Nexthink, OEM APIs)", "Provision test tenants + API keys"] },
  { phase: "Week 3-6", title: "Core build (persona catalogue, order, approval)", items: ["Implement per-persona catalogue + OrderDialog equivalent", "Wire real ServiceNow REST (payload already 1-to-1)", "Configure Teams/Outlook approvals or public token flow"] },
  { phase: "Week 7-9", title: "Ecosystem + Analytics", items: ["Wire Nexthink telemetry + self-heal actions", "Build Refresh Radar (age > 36mo)", "Publish Power BI dashboards or SN reports"] },
  { phase: "Week 10-12", title: "AI + Sustainability + Hardening", items: ["Ground Copilot in live data (RAG or context injection)", "Add Buyback quote + certified disposal workflow", "Security review + penetration test", "Go-live playbook + rollback plan"] },
  { phase: "Ongoing", title: "Iteration", items: ["Feedback loop from personas", "Add new OEMs / new personas / new policies", "Quarterly Copilot fine-tuning + policy tune"] },
];

function OptionCard({ opt }) {
  return (
    <Card data-testid={`deploy-option-${opt.id}`} className={`p-6 border-slate-200 shadow-sm ${opt.accent ? "border-[#0033A0] ring-2 ring-[#0033A0]/15" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display font-bold text-lg text-slate-900">{opt.name}</div>
          <div className="text-xs text-slate-500">{(opt.tag || "").replace(/Chevron/g, opt.__org || "Chevron")}</div>
        </div>
        {opt.accent && <Badge className="bg-[#0033A0] hover:bg-[#0033A0] text-white text-[10px]">Live now</Badge>}
      </div>
      <div className="mt-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Stack</div>
        <div className="mt-1 flex flex-wrap gap-1">
          {opt.stack.map((s) => (
            <span key={s} className="text-[11px] border border-slate-200 bg-slate-50 rounded px-1.5 py-0.5 text-slate-700">{s.replace(/Chevron/g, opt.__org || "Chevron")}</span>
          ))}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-green-700 font-semibold">Pros</div>
          <ul className="mt-1 space-y-1">
            {opt.pros.map((p) => <li key={p} className="text-xs text-slate-700 flex items-start gap-1.5"><CheckCircle2 size={12} className="text-green-600 shrink-0 mt-0.5" /> {p.replace(/Chevron/g, opt.__org || "Chevron")}</li>)}
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold">Cons</div>
          <ul className="mt-1 space-y-1">
            {opt.cons.map((c) => <li key={c} className="text-xs text-slate-700 flex items-start gap-1.5"><XCircle size={12} className="text-[#DA291C] shrink-0 mt-0.5" /> {c.replace(/Chevron/g, opt.__org || "Chevron")}</li>)}
          </ul>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between text-xs">
        <span className="text-slate-500">Effort: <span className="font-semibold text-slate-900">{opt.effort}</span></span>
        <span className="text-slate-500">Owner: <span className="font-semibold text-slate-900">{opt.ownership}</span></span>
      </div>
    </Card>
  );
}

export default function Blueprint() {
  const { brand } = useBranding();
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const brandBlue = brand?.primary || "#0033A0";
  const brandSoft = brand?.primarySoft || "#0033A020";

  return (
    <div className="space-y-10" data-testid="blueprint-page">
      {/* Hero */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
            <Layers size={12} style={{ color: brandBlue }} /> Architecture
          </div>
          <h1 className="font-display font-black text-4xl text-slate-900 tracking-tight mt-2 leading-tight">
            Flexspace is the intelligence layer on top of your existing stack —
            <span style={{ color: brandBlue }}> not a replacement for it.</span>
          </h1>
          <p className="text-[15px] text-slate-600 mt-3 max-w-2xl leading-7">
            Every signal from {brand.orgName}&apos;s existing tools flows up into six purpose-built agents. Every decision flows back down as an action in ServiceNow, Ariba, Intune, or the OEM. Employees and executives only see one surface.
          </p>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-2">
          <div className="flex flex-wrap justify-end gap-2">
            <Button asChild variant="outline" data-testid="leadership-pdf-btn" className="border-[#DA291C]/40 text-[#DA291C] hover:bg-[#DA291C]/5">
              <a href="/leaders_deck/Flexspace-Leadership-Screenshots.pdf" download="Flexspace-Leadership-Screenshots.pdf">
                <Download size={14} className="mr-2" /> Leadership PDF
              </a>
            </Button>
            <Button asChild data-testid="download-deck-btn" className="text-white shadow-sm" style={{ backgroundColor: brandBlue }}>
              <a href="/flexspace_deck.pptx" download={`${brand.label}-Flexspace-Deck.pptx`}>
                <Download size={14} className="mr-2" /> Slide Deck
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Three-band architecture map */}
      <ArchitectureMap brand={brand} brandBlue={brandBlue} brandSoft={brandSoft} />

      {/* Before / After */}
      <BeforeAfter brandBlue={brandBlue} brandSoft={brandSoft} />

      {/* Delivery options appendix (collapsed by default) */}
      <Collapsible open={deliveryOpen} onOpenChange={setDeliveryOpen}>
        <CollapsibleTrigger asChild>
          <button
            className="w-full flex items-center justify-between rounded-2xl bg-white p-5 hover:bg-slate-50 transition-colors"
            style={{ border: `1px solid ${brandSoft}` }}
            data-testid="delivery-options-toggle"
          >
            <div className="text-left">
              <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500">Appendix</div>
              <div className="text-lg font-semibold text-slate-900 mt-0.5">Delivery options · how would we build this at {brand.orgName}?</div>
              <div className="text-xs text-slate-500 mt-0.5">React + FastAPI (current) · ServiceNow-native · Power Platform · BigFix — with component mapping &amp; a 12-week hand-off plan.</div>
            </div>
            <ChevronDown size={20} className="text-slate-400 shrink-0" style={{ transform: deliveryOpen ? "rotate(180deg)" : "none", transition: "transform 200ms" }} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-6 mt-6">
          <div className="grid md:grid-cols-2 gap-4" data-testid="deploy-options-grid">
            {OPTIONS.map((o) => <OptionCard key={o.id} opt={{ ...o, __org: brand.orgName }} />)}
          </div>

          <Card className="p-6 border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              <GitBranch size={12} style={{ color: brandBlue }} /> Component Mapping
            </div>
            <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Same product, four platforms</h2>
            <p className="text-xs text-slate-500 mt-1">How every Flexspace capability translates across each target stack.</p>
            <div className="mt-4 border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm" data-testid="mapping-table">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-semibold">Flexspace capability</th>
                    <th className="text-left px-4 py-2.5 font-semibold">ServiceNow-native</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Power Platform</th>
                    <th className="text-left px-4 py-2.5 font-semibold">BigFix</th>
                  </tr>
                </thead>
                <tbody>
                  {MAPPING.map((m) => (
                    <tr key={m.flex} className="border-t border-slate-200 hover:bg-slate-50">
                      <td className="px-4 py-2.5 font-medium text-slate-900">{m.flex}</td>
                      <td className="px-4 py-2.5 text-slate-700 text-xs">{m.sn}</td>
                      <td className="px-4 py-2.5 text-slate-700 text-xs">{m.power}</td>
                      <td className="px-4 py-2.5 text-slate-700 text-xs">{m.bigfix}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="p-6 border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              <Zap size={12} style={{ color: brandBlue }} /> Hand-off Pathway
            </div>
            <h2 className="font-display font-bold text-xl text-slate-900 mt-1">POC to production in 12 weeks</h2>
            <div className="mt-5 relative pl-8" data-testid="handoff-timeline">
              <div className="absolute left-3 top-2 bottom-2 w-px bg-slate-200" />
              {HANDOFF.map((h, i) => (
                <div key={h.phase} className="relative pb-6 last:pb-0">
                  <div className="absolute -left-6 top-1 w-4 h-4 rounded-full grid place-items-center text-white text-[10px] font-bold" style={{ backgroundColor: brandBlue }}>{i + 1}</div>
                  <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: brandBlue }}>{h.phase}</div>
                  <div className="font-display font-bold text-slate-900">{h.title}</div>
                  <ul className="mt-1.5 space-y-1">
                    {h.items.map((it) => <li key={it} className="text-xs text-slate-600 flex items-start gap-1.5"><ArrowRight size={11} className="text-slate-400 shrink-0 mt-0.5" /> {it.replace(/Chevron/g, brand.orgName)}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

// ---------- Three-band architecture map ----------

const SURFACES = [
  { id: "portal",  icon: Users,       name: "Employee Portal",  sub: "Catalog · Marketplace · MyPC · Orders" },
  { id: "cockpit", icon: LineChart,   name: "Admin Cockpit",    sub: "Fleet KPIs · joiners · warehouse" },
  { id: "copilot", icon: MessageSquare, name: "Copilot",        sub: "Ask any agent · natural language" },
];

const AGENTS = [
  { id: "plan",     num: "01", name: "Demand",     sub: "Forecast + planning" },
  { id: "source",   num: "02", name: "Supply",     sub: "Sourcing + POs" },
  { id: "deploy",   num: "03", name: "Fulfilment", sub: "Deploy to Day-1" },
  { id: "operate",  num: "04", name: "Device",     sub: "In-life & Device UX" },
  { id: "refresh",  num: "05", name: "Refresh",    sub: "Extend or replace" },
  { id: "recover",  num: "06", name: "Recovery",   sub: "Wipe + resell" },
];

const STACK = [
  { name: "ServiceNow",  role: "HAM · ITSM" },
  { name: "Workday",     role: "Joiners · exits · roles" },
  { name: "SAP HR",      role: "Employment records" },
  { name: "SAP Ariba",   role: "POs · contracts" },
  { name: "Intune / Jamf", role: "UEM · image · apps" },
  { name: "Nexthink",    role: "DEX · telemetry" },
  { name: "Blancco",     role: "Certified wipe" },
  { name: "OEMs · DHL/UPS · ITAD", role: "Buy · ship · resell" },
];

function ArchitectureMap({ brand, brandBlue, brandSoft }) {
  return (
    <div className="rounded-2xl bg-white p-6 md:p-10 relative" style={{ border: `1px solid ${brandSoft}` }} data-testid="architecture-map">
      {/* Top band · Surfaces */}
      <div className="mb-2">
        <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500 mb-3">Surfaces · one experience</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {SURFACES.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.id} className="rounded-xl p-4 flex items-start gap-3" style={{ backgroundColor: brandSoft }} data-testid={`arch-surface-${s.id}`}>
                <div className="w-9 h-9 rounded-lg bg-white grid place-items-center shrink-0"><Icon size={16} style={{ color: brandBlue }} /></div>
                <div className="min-w-0"><div className="text-sm font-semibold text-slate-900">{s.name}</div><div className="text-[11px] text-slate-500 mt-0.5">{s.sub}</div></div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Down-arrow between bands · decisions flow down */}
      <BandArrows brandBlue={brandBlue} label="decisions flow down" />

      {/* Middle band · Six Flexspace agents */}
      <div className="rounded-2xl p-4 md:p-6 relative overflow-hidden mb-2" style={{ background: `linear-gradient(135deg, ${brandBlue}, ${brandBlue}dd)` }} data-testid="arch-band-agents">
        <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-white/80 mb-3 flex items-center gap-1.5">
          <Sparkles size={12} /> Flexspace · persistent Lifecycle Context across six agents
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {AGENTS.map((a) => (
            <div key={a.id} className="rounded-lg bg-white/95 p-3" data-testid={`arch-agent-${a.id}`}>
              <div className="text-[10px] font-mono font-semibold" style={{ color: brandBlue }}>{a.num}</div>
              <div className="text-sm font-semibold text-slate-900">{a.name}</div>
              <div className="text-[10px] text-slate-500 truncate">{a.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Up-arrow between bands · signals flow up */}
      <BandArrows brandBlue={brandBlue} label="signals flow up" direction="up" />

      {/* Bottom band · Existing stack */}
      <div>
        <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500 mb-3">{brand.orgName}&apos;s existing stack</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {STACK.map((s) => (
            <div key={s.name} className="rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid={`arch-stack-${s.name.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
              <div className="text-sm font-semibold text-slate-900 truncate">{s.name}</div>
              <div className="text-[11px] text-slate-500 truncate mt-0.5">{s.role}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BandArrows({ brandBlue, label, direction = "down" }) {
  const Arrow = direction === "up" ? ArrowUp : ArrowDown;
  return (
    <div className="flex items-center justify-center gap-3 py-3">
      <div className="h-px flex-1 bg-slate-200" />
      <div className="flex items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase font-semibold text-slate-500">
        <Arrow size={12} style={{ color: brandBlue }} />
        {label}
      </div>
      <div className="h-px flex-1 bg-slate-200" />
    </div>
  );
}

// ---------- Before / After ----------

function BeforeAfter({ brandBlue, brandSoft }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="before-after">
      {/* Before */}
      <div className="rounded-2xl bg-white p-6 md:p-8" style={{ border: `1px solid #FECACA` }} data-testid="before-card">
        <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-red-700 mb-2 flex items-center gap-1.5">
          <XCircle size={12} /> Today · six disconnected tools
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-4">Every joiner cohort re-derived by hand.</h3>
        <ul className="space-y-2.5">
          <li className="flex items-start gap-2 text-sm text-slate-700"><span className="text-red-600 shrink-0">·</span> Procurement raises a PO for <strong>570</strong> devices — no idea how many are already in returns pipeline</li>
          <li className="flex items-start gap-2 text-sm text-slate-700"><span className="text-red-600 shrink-0">·</span> IT ops runs a fleet-wide 3-year refresh — <strong>117 healthy</strong> devices get swapped anyway</li>
          <li className="flex items-start gap-2 text-sm text-slate-700"><span className="text-red-600 shrink-0">·</span> Nexthink flags Osaka Line 3 is trending bad — takes 4 weeks for anyone to see it</li>
          <li className="flex items-start gap-2 text-sm text-slate-700"><span className="text-red-600 shrink-0">·</span> HR exit devices get scrapped by default — <strong>$186K</strong> of resale value lost</li>
          <li className="flex items-start gap-2 text-sm text-slate-700"><span className="text-red-600 shrink-0">·</span> Each of six teams keeps its own spreadsheet · no one owns the loop</li>
        </ul>
      </div>

      {/* After */}
      <div className="rounded-2xl bg-white p-6 md:p-8" style={{ border: `1px solid ${brandSoft}` }} data-testid="after-card">
        <div className="text-[11px] tracking-[0.14em] uppercase font-semibold mb-2 flex items-center gap-1.5" style={{ color: brandBlue }}>
          <CheckCircle2 size={12} /> With Flexspace · one persistent context
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-4">Six agents. One loop. One number that adds up.</h3>
        <ul className="space-y-2.5 mb-5">
          <li className="flex items-start gap-2 text-sm text-slate-700"><span className="shrink-0" style={{ color: brandBlue }}>·</span> Demand converts <strong>570 joiners → 668</strong> net devices (+186 aging assets · −34 retained · −54 policy)</li>
          <li className="flex items-start gap-2 text-sm text-slate-700"><span className="shrink-0" style={{ color: brandBlue }}>·</span> Supply covers <strong>386 of 668 internally</strong> · 282 OEM (was 570)</li>
          <li className="flex items-start gap-2 text-sm text-slate-700"><span className="shrink-0" style={{ color: brandBlue }}>·</span> Fulfilment lands <strong>626 Day-1 ready</strong> · 42 at-risk each with a specific SN task</li>
          <li className="flex items-start gap-2 text-sm text-slate-700"><span className="shrink-0" style={{ color: brandBlue }}>·</span> Refresh keeps <strong>117 healthy</strong> devices in service · $612K avoided</li>
          <li className="flex items-start gap-2 text-sm text-slate-700"><span className="shrink-0" style={{ color: brandBlue }}>·</span> Recovery redeploys <strong>214 grade-A</strong> back into next quarter&apos;s Supply plan</li>
        </ul>
        <div className="grid grid-cols-3 gap-3 pt-5 border-t border-slate-100">
          <div><div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Cost avoided</div><div className="text-xl font-semibold" style={{ color: brandBlue }}>$855K</div></div>
          <div><div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Recovered</div><div className="text-xl font-semibold" style={{ color: brandBlue }}>$186K</div></div>
          <div><div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Net impact</div><div className="text-xl font-semibold" style={{ color: brandBlue }}>$1.04M</div></div>
        </div>
      </div>
    </div>
  );
}
